import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { ArrowLeft, MapPin, Camera, Loader2, CheckCircle2, AlertCircle, Search, UserPlus, Check, X, LogOut } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const GPS_STATUS = { IDLE: 'idle', LOADING: 'loading', SUCCESS: 'success', ERROR: 'error' };

export const FieldLeadForm = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const isLeadFeeder = user?.role === 'lead_feeder';

  const [formData, setFormData] = useState({
    klient: '',
    telefon_klienta: '',
    adres_inwestycji: '',
    notatki_terenowe: '',
    source: 'Trasa',
    client_id: '',
    build_stage: '',
    build_comment: ''
  });

  const [gps, setGps] = useState({ lat: null, lng: null, accuracy: null, status: GPS_STATUS.IDLE, error: '' });
  const [photos, setPhotos] = useState([]);

  // Client autocomplete
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const clientRef = useRef(null);
  const searchTimeout = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (clientRef.current && !clientRef.current.contains(e.target)) setShowClientDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const searchClients = useCallback(async (q) => {
    if (q.length < 2) { setClientResults([]); return; }
    try {
      const res = await axios.get(`${API_URL}/clients/search`, { params: { q } });
      setClientResults(res.data);
      setShowClientDropdown(true);
    } catch (err) { console.error("Search error:", err); setClientResults([]); }
  }, []);

  const handleClientSearchChange = (e) => {
    const val = e.target.value;
    setClientSearch(val);
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, klient: val, client_id: '' }));
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => searchClients(val), 300);
  };

  const selectClient = (c) => {
    setSelectedClient(c);
    setClientSearch(c.nazwa);
    setShowClientDropdown(false);
    setFormData(prev => ({
      ...prev,
      klient: c.nazwa,
      telefon_klienta: c.telefon || prev.telefon_klienta,
      adres_inwestycji: c.adres || prev.adres_inwestycji,
      client_id: c.id
    }));
    toast.success(`Dane klienta "${c.nazwa}" wypełnione`);
  };

  const getGPSLocation = () => {
    if (!navigator.geolocation) {
      setGps(prev => ({ ...prev, status: GPS_STATUS.ERROR, error: 'GPS niedostępny w tej przeglądarce' }));
      return;
    }
    setGps(prev => ({ ...prev, status: GPS_STATUS.LOADING, error: '' }));

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setGps({ lat: latitude, lng: longitude, accuracy: Math.round(accuracy), status: GPS_STATUS.SUCCESS, error: '' });

        // Reverse geocoding via Nominatim
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=pl`,
            { headers: { 'User-Agent': 'TrendhomesCRM/1.0' } }
          );
          const data = await res.json();
          if (data.display_name) {
            setFormData(prev => ({ ...prev, adres_inwestycji: data.display_name }));
          }
        } catch (err) { console.error("Geocoding error:", err);
          // Geocoding failed — user can type address manually
        }
      },
      (error) => {
        let msg = 'Nie udało się pobrać lokalizacji';
        if (error.code === 1) msg = 'Brak zgody na lokalizację. Wpisz adres ręcznie.';
        else if (error.code === 2) msg = 'Lokalizacja niedostępna. Wpisz adres ręcznie.';
        else if (error.code === 3) msg = 'Przekroczono czas oczekiwania. Spróbuj ponownie.';
        setGps(prev => ({ ...prev, status: GPS_STATUS.ERROR, error: msg }));
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (index) => {
    setPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasGPS = gps.lat && gps.lng;
    const hasAddress = formData.adres_inwestycji.trim();
    if (!hasGPS && !hasAddress) {
      toast.error('Podaj lokalizację — użyj GPS lub wpisz adres');
      return;
    }
    if (!formData.build_stage) {
      toast.error('Wybierz etap budowy');
      return;
    }
    if (formData.build_stage === 'Inny' && !formData.build_comment.trim()) {
      toast.error('Dla etapu „Inny" opisz, jaki to etap');
      return;
    }

    setLoading(true);
    try {
      let clientId = formData.client_id;

      // Auto-create client only if name provided
      if (!clientId && formData.klient.trim()) {
        try {
          const clientRes = await axios.post(`${API_URL}/clients`, {
            nazwa: formData.klient,
            telefon: formData.telefon_klienta || '',
            adres: formData.adres_inwestycji || ''
          });
          clientId = clientRes.data.id;
        } catch (err) { console.error("Operation failed:", err); }
      }

      const payload = {
        klient: formData.klient,
        adres_inwestycji: formData.adres_inwestycji,
        telefon_klienta: formData.telefon_klienta || '',
        notatki_terenowe: formData.notatki_terenowe || '',
        client_id: clientId || null,
        source: formData.source,
        location_gps: gps.lat ? { lat: gps.lat, lng: gps.lng, accuracy: gps.accuracy } : null,
        build_stage: formData.build_stage,
        build_comment: formData.build_comment || ''
      };

      let entityId;
      // All field leads go to lead_submissions (skrzynka leadów)
      const res = await axios.post(`${API_URL}/lead-inbox/submissions`, payload);
      entityId = res.data.id;

      // Upload photos
      if (photos.length > 0) {
        for (const photo of photos) {
          const fd = new FormData();
          fd.append('file', photo.file);
          try {
            await axios.post(`${API_URL}/lead-inbox/submissions/${entityId}/photos`, fd);
          } catch (err) { console.error("Non-blocking error:", err); }
        }
      }

      toast.success('Lead przesłany do skrzynki!');
      if (isLeadFeeder) {
        setFormData({ klient: '', telefon_klienta: '', adres_inwestycji: '', notatki_terenowe: '', source: 'Trasa', client_id: '', build_stage: '', build_comment: '' });
        setGps({ lat: null, lng: null, accuracy: null, status: GPS_STATUS.IDLE, error: '' });
        setPhotos([]);
        setClientSearch('');
        setSelectedClient(null);
      } else {
        navigate('/skrzynka-leadow');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd tworzenia leada');
    } finally {
      setLoading(false);
    }
  };

  const handleLeadFeederLogout = () => {
    logout();
    navigate('/login');
  };

  const formContent = (
    <div className={isLeadFeeder ? "min-h-screen bg-[var(--crm-bg-main)] text-[var(--crm-text-primary)] overflow-auto pb-24" : ""}>
      <div className="p-4 md:p-8 lg:p-12 max-w-2xl mx-auto">
        <div className="mb-6">
          {isLeadFeeder ? (
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 text-sm text-[var(--crm-text-secondary)]">
                <MapPin className="w-4 h-4 text-[#FF6B35]" />
                <span>Trendhomes — Lead Feeder</span>
              </div>
              <Button
                onClick={handleLeadFeederLogout}
                data-testid="lead-feeder-logout-button"
                className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-red-400 border border-[var(--crm-border)] text-sm px-3 py-1 h-auto"
              >
                <LogOut className="w-3.5 h-3.5 mr-1.5" />
                Wyloguj
              </Button>
            </div>
          ) : (
            <Button
              onClick={() => navigate(-1)}
              data-testid="field-lead-back-button"
              className="bg-transparent hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-secondary)] hover:text-[var(--crm-text-primary)] border border-[var(--crm-border)] mb-4"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Powrót
            </Button>
          )}
          <h1 className="text-2xl md:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight">Nowy Lead z trasy</h1>
          <p className="text-[var(--crm-text-secondary)] mt-1 text-sm">Wystarczy sama lokalizacja — reszta jest opcjonalna</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Client name */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
            <div ref={clientRef} className="relative">
              <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">Imię i nazwisko klienta <span className="font-normal text-[var(--crm-text-muted)]">(opcjonalne)</span></Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--crm-text-secondary)] pointer-events-none" />
                <Input
                  value={clientSearch || formData.klient}
                  onChange={handleClientSearchChange}
                  onFocus={() => clientSearch.length >= 2 && setShowClientDropdown(true)}
                  data-testid="field-lead-client-input"
                  placeholder="Jan Kowalski"
                  autoComplete="off"
                  className="pl-10 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-12 text-base"
                />
                {selectedClient && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-emerald-400" />
                )}
              </div>
              {showClientDropdown && clientResults.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  {clientResults.map(c => (
                    <button key={c.id} type="button" onClick={() => selectClient(c)}
                      className="w-full text-left px-4 py-3 hover:bg-[#FF6B35]/10 transition-colors border-b border-[var(--crm-border)]/50 last:border-b-0">
                      <span className="text-[var(--crm-text-primary)] font-medium">{c.nazwa}</span>
                      <span className="text-[var(--crm-text-secondary)] text-xs block">
                        {[c.telefon, c.adres].filter(Boolean).join(' · ') || 'Brak danych'}
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {!selectedClient && clientSearch.length >= 2 && clientResults.length === 0 && showClientDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-[var(--crm-bg-main)] border border-[var(--crm-border)] rounded-lg shadow-xl p-3">
                  <p className="text-[var(--crm-text-secondary)] text-sm flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-[#FF6B35]" />
                    Nowy klient zostanie utworzony automatycznie
                  </p>
                </div>
              )}
            </div>

            {/* Phone */}
            <div className="mt-3">
              <Label className="text-[var(--crm-text-secondary)] text-sm">Telefon</Label>
              <Input
                type="tel"
                value={formData.telefon_klienta}
                onChange={(e) => setFormData(prev => ({ ...prev, telefon_klienta: e.target.value }))}
                data-testid="field-lead-phone-input"
                placeholder="+48 123 456 789"
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-12 text-base mt-1"
              />
            </div>
          </Card>

          {/* GPS Location */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4" data-testid="field-lead-gps-section">
            <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">Lokalizacja *</Label>

            <Button
              type="button"
              onClick={getGPSLocation}
              disabled={gps.status === GPS_STATUS.LOADING}
              data-testid="field-lead-gps-button"
              className="w-full mt-2 h-14 text-base font-semibold bg-[#F59E0B] hover:bg-[#D97706] text-[var(--crm-text-primary)] rounded-xl transition-all active:scale-[0.98]"
            >
              {gps.status === GPS_STATUS.LOADING ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Pobieranie lokalizacji...</>
              ) : gps.status === GPS_STATUS.SUCCESS ? (
                <><CheckCircle2 className="w-5 h-5 mr-2" />Lokalizacja pobrana — odśwież</>
              ) : (
                <><MapPin className="w-5 h-5 mr-2" />Pobierz moją lokalizację</>
              )}
            </Button>

            {/* GPS status */}
            {gps.status === GPS_STATUS.SUCCESS && (
              <div className="mt-2 flex items-center gap-2 text-emerald-400 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>GPS: {gps.lat?.toFixed(6)}, {gps.lng?.toFixed(6)} · Dokładność: ±{gps.accuracy}m</span>
              </div>
            )}
            {gps.status === GPS_STATUS.ERROR && (
              <div className="mt-2 flex items-center gap-2 text-red-400 text-xs">
                <AlertCircle className="w-3.5 h-3.5" />
                <span>{gps.error}</span>
              </div>
            )}

            {/* Map preview */}
            {gps.lat && gps.lng && (
              <div className="mt-3 rounded-lg overflow-hidden border border-[var(--crm-border)]" data-testid="field-lead-map-preview">
                <iframe
                  title="Lokalizacja"
                  width="100%"
                  height="180"
                  style={{ border: 0 }}
                  loading="lazy"
                  src={`https://maps.google.com/maps?q=${gps.lat},${gps.lng}&z=16&output=embed`}
                />
              </div>
            )}

            {/* Address field */}
            <div className="mt-3">
              <Label className="text-[var(--crm-text-secondary)] text-xs">Adres (edytuj jeśli GPS się pomylił)</Label>
              <Input
                value={formData.adres_inwestycji}
                onChange={(e) => setFormData(prev => ({ ...prev, adres_inwestycji: e.target.value }))}
                data-testid="field-lead-address-input"
                placeholder="ul. Przykładowa 10, Warszawa"
                className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] h-12 text-base mt-1"
              />
            </div>
          </Card>

          {/* Notes */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
            <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">Notatki</Label>
            <Textarea
              value={formData.notatki_terenowe}
              onChange={(e) => setFormData(prev => ({ ...prev, notatki_terenowe: e.target.value }))}
              data-testid="field-lead-notes-input"
              placeholder="np. duża budowa, 15 okien, klient chce PVC..."
              rows={3}
              className="bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)] text-base mt-1"
            />
          </Card>

          {/* Source */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
            <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">Źródło leada</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {['Trasa', 'Polecenie', 'Networking', 'Przetarg', 'Internet', 'Inne'].map(src => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, source: src }))}
                  data-testid={`field-lead-source-${src.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    formData.source === src
                      ? 'bg-[#FF6B35] text-white'
                      : 'bg-[var(--crm-bg-panel)] text-[var(--crm-text-secondary)] border border-[var(--crm-border)] hover:border-slate-400'
                  }`}
                >
                  {src}
                </button>
              ))}
            </div>
          </Card>

          {/* Photos */}
          <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-4">
            <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">Zdjęcia z terenu</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhotoCapture}
              className="hidden"
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              data-testid="field-lead-photo-button"
              className="w-full mt-2 h-12 bg-[var(--crm-bg-btn-secondary)] hover:bg-[var(--crm-bg-card-hover)] text-[var(--crm-text-primary)] rounded-xl"
            >
              <Camera className="w-5 h-5 mr-2" />
              {photos.length > 0 ? `Dodaj kolejne (${photos.length} zdjęć)` : 'Zrób / dodaj zdjęcie'}
            </Button>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-3">
                {photos.map((photo, i) => (
                  <div key={`${photo.name}-${photo.lastModified}-${i}`} className="relative aspect-square rounded-lg overflow-hidden border border-[var(--crm-border)]">
                    <img src={photo.preview} alt={`Zdjęcie ${i + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3.5 h-3.5 text-[var(--crm-text-primary)]" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Build Stage */}
          <Card className="bg-[var(--crm-bg-card)] border-[var(--crm-border)] p-5">
            <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold mb-3 block">Etap budowy *</Label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'SSO', label: 'SSO', desc: 'Stan surowy otwarty', color: 'border-orange-400 bg-orange-500/10 text-orange-500' },
                { value: 'SSZ', label: 'SSZ', desc: 'Stan surowy zamknięty', color: 'border-emerald-400 bg-emerald-500/10 text-emerald-500' },
                { value: 'Inny', label: 'Inny', desc: 'Inny etap', color: 'border-[var(--crm-text-muted)] bg-[var(--crm-bg-panel)] text-[var(--crm-text-secondary)]' },
              ].map(opt => {
                const active = formData.build_stage === opt.value;
                return (
                  <button key={opt.value} type="button"
                    data-testid={`build-stage-${opt.value}`}
                    onClick={() => setFormData(prev => ({ ...prev, build_stage: opt.value }))}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      active ? opt.color + ' ring-2 ring-offset-1 ring-offset-[var(--crm-bg-card)]' : 'border-[var(--crm-border)] hover:border-[var(--crm-text-muted)]'
                    }`}
                  >
                    <p className={`font-bold text-lg ${active ? '' : 'text-[var(--crm-text-primary)]'}`}>{opt.label}</p>
                    <p className={`text-[10px] mt-0.5 ${active ? '' : 'text-[var(--crm-text-muted)]'}`}>{opt.desc}</p>
                  </button>
                );
              })}
            </div>
            {formData.build_stage && (
              <div className="mt-3">
                <Label className="text-[var(--crm-text-secondary)] text-sm font-semibold">
                  Komentarz / opis {formData.build_stage === 'Inny' ? '*' : <span className="font-normal text-[var(--crm-text-muted)]">(opcjonalnie)</span>}
                </Label>
                <Textarea
                  value={formData.build_comment}
                  onChange={e => setFormData(prev => ({ ...prev, build_comment: e.target.value }))}
                  data-testid="build-comment-input"
                  placeholder={formData.build_stage === 'Inny' ? 'Opisz etap budowy...' : 'np. duży dom, 12 okien, tynki na elewacji...'}
                  rows={2}
                  className="bg-[var(--crm-bg-input)] border-[var(--crm-border)] text-[var(--crm-text-primary)] mt-1"
                />
              </div>
            )}
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={loading || !formData.build_stage || (formData.build_stage === 'Inny' && !formData.build_comment.trim())}
            data-testid="field-lead-submit-button"
            className="w-full h-14 text-lg font-bold bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white rounded-xl transition-all active:scale-[0.98]"
          >
            {loading ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Zapisywanie...</>
            ) : (
              'Wyślij Lead'
            )}
          </Button>
        </form>
      </div>
    </div>
  );

  if (isLeadFeeder) {
    return formContent;
  }

  return <Layout>{formContent}</Layout>;
};
